import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useInvitationService } from '../services/invitation.service';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/atoms/Button';
import { useAuth0 } from '@auth0/auth0-react';

type InvitationParams = {
  token: string;
};

function InvitationCompletePage() {
  const { token } = useParams() as InvitationParams;
  const navigate = useNavigate();
  const { validateInvitation, acceptInvitation } = useInvitationService();
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const { t } = useTranslation('auth');
  
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [email, setEmail] = useState('');
  const [acceptSuccess, setAcceptSuccess] = useState(false);
  const completionInProgress = useRef(false);
  
  // First check if user is authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      // User is not authenticated, redirect them back to the invitation page
      navigate(`/invite/${token}`);
      return;
    }
  }, [authLoading, isAuthenticated, navigate, token]);
  
  // Validate the token and accept the invitation
  useEffect(() => {
    const completeInvitation = async () => {
      if (!token || authLoading || !isAuthenticated || completionInProgress.current) return;
      
      completionInProgress.current = true;
            
      try {
        // First validate the invitation
        const invitation = await validateInvitation(token);
        setIsValid(true);
        setEmail(invitation.email);
        
        // If invitation is already accepted, just redirect to dashboard
        if (invitation.is_accepted) {
          setAcceptSuccess(true);
          toast.success(t('toasts.invitation_already_processed'));
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        }
        
        // Check if the invitation expiration is in the past
        const expirationDate = new Date(invitation.expires_at);
        if (expirationDate < new Date()) {
          toast.error(t('toasts.invitation_expired'));
          setIsValid(false);
          return;
        }
        
        // Accept the invitation now that user is authenticated
        await acceptInvitation(token, { authenticated: true });
        setAcceptSuccess(true);
        toast.success(t('toasts.invitation_accept_success'));
        
        // Redirect to dashboard after a short delay
        setTimeout(() => navigate('/dashboard'), 3000);
        
      } catch (error) {
        console.error('Failed to complete invitation:', error);
        toast.error(t('toasts.invitation_complete_failed'));
        setIsValid(false);
      } finally {
        setIsValidating(false);
        completionInProgress.current = false;
      }
    };
    
    completeInvitation();
  }, [token, authLoading, isAuthenticated, navigate]); // Remove function dependencies
  
  // Loading state
  if (authLoading || isValidating) {
    return (
      <Layout className="bg-white" hideFooter={true}>
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent border-purple mb-3"></div>
          <p className="text-dark-invirtus font-body">{t('invitation_complete.completing')}</p>
        </div>
      </Layout>
    );
  }

  // Invalid token or error state
  if (!isValid && !isValidating) {
    return (
      <Layout className="bg-white" hideFooter={true}>
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <svg className="w-12 h-12 text-red mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <h1 className="text-xl font-semibold text-dark-invirtus mb-2 font-heading">{t('invitation_complete.error_title')}</h1>
          <p className="text-dark-invirtus mb-4 text-center font-body">{t('invitation_complete.error_desc')}</p>
          <Button
            label={t('invitation_complete.go_to_dashboard')}
            onClick={() => navigate('/dashboard')}
            intent="primary"
            type="contained"
          />
        </div>
      </Layout>
    );
  }

  // Success state
  return (
    <Layout className="bg-white" hideFooter={true}>
      <div className="flex flex-col items-center justify-center py-16 px-4 max-w-md mx-auto">
        {acceptSuccess ? (
          <>
            <svg className="w-12 h-12 text-green-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <h1 className="text-xl font-semibold text-dark-invirtus mb-2 font-heading">{t('invitation_complete.success_title')}</h1>
            <p className="text-dark-invirtus mb-4 text-center font-body">
              {t('invitation_complete.success_desc')}
            </p>
            <div className="w-full bg-gray-200 h-2 rounded overflow-hidden">
              <div className="bg-purple h-full animate-[fillBar_3s_linear_forwards]"></div>
            </div>
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent border-purple mb-3"></div>
            <h1 className="text-xl font-semibold text-dark-invirtus mb-2 font-heading">{t('invitation_complete.accepting_title')}</h1>
            <p className="text-dark-invirtus mb-4 text-center font-body">
              {t('invitation_complete.accepting_desc', { email: <span className="text-purple font-medium">{email}</span> })}
            </p>
          </>
        )}
      </div>
    </Layout>
  );
}

export default InvitationCompletePage;